/**
 * API-Client.
 *
 * Kümmert sich um genau zwei Dinge, die man sonst überall wiederholt und irgendwo
 * vergisst: das CSRF-Token in den Header schreiben und Serverfehler in etwas
 * verwandeln, das man einem Menschen zeigen kann.
 */
import { t } from './i18n/t.js'

const CSRF_COOKIE = '__Host-fh_csrf'
const CSRF_HEADER = 'X-FH-CSRF'

export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
    readonly requestId?: string,
    /**
     * Maschinenlesbare Zusatzinfo vom Server — etwa die ID des Anrufs, der in
     * diesem Raum schon läuft. Wurde bisher weggeworfen: Der Server schickte
     * sie, der Client kannte das Feld nicht, und die Oberfläche konnte nur
     * eine allgemeine Meldung zeigen, wo eine Handlung möglich gewesen wäre.
     */
    readonly data?: Record<string, string | number>,
  ) {
    super(message)
    this.name = 'ApiError'
  }

  /** Sitzung abgelaufen oder widerrufen — die App muss zur Anmeldung. */
  get needsLogin(): boolean {
    return this.status === 401
  }
}

/** Wird auch vom SOS-Weg gebraucht, der bewusst an `request()` vorbeigeht. */
export function csrfToken(): string {
  const match = document.cookie.match(new RegExp(`(?:^|; )${CSRF_COOKIE}=([^;]*)`))
  return match ? decodeURIComponent(match[1]!) : ''
}

interface ErrorBody {
  error?: {
    code?: string; message?: string; requestId?: string
    data?: Record<string, string | number>
  }
}

/**
 * Antwort auswerten — auch wenn sie gar kein JSON ist.
 *
 * ## Warum das nötig ist
 *
 * Nicht jede Antwort kommt von unserem Server. nginx antwortet mit einer
 * **HTML-Seite**, wenn es selbst entscheidet: 503 beim Rate-Limit, 502 während
 * der Dienst neu startet, 504 bei Zeitüberschreitung, 413 bei zu großem Body.
 *
 * Vorher stand `JSON.parse(text)` VOR der Prüfung auf `res.ok`. Aus einem
 * ehrlichen „zu viele Anfragen" wurde damit ein
 * `SyntaxError: Unexpected token '<'` — mitten in der Oberfläche, ohne Bezug
 * zur Ursache. Beim ersten echten Durchlauf genau so passiert, nachgewiesen im
 * nginx-Log („limiting requests, excess: 20.490 by zone fh_api").
 */
function menschlich(status: number): string {
  if (status === 429 || status === 503) return t('Gerade zu viele Anfragen. Bitte kurz warten.')
  if (status === 502 || status === 504) return t('Der Server antwortet nicht. Gleich noch einmal versuchen.')
  if (status === 413) return t('Das war zu groß.')
  return t('Da ist etwas schiefgelaufen.')
}

async function auswerten<T>(res: Response): Promise<T> {
  if (res.status === 204) return undefined as T

  const text = await res.text()
  let data: unknown = null
  let jsonLesbar = true
  try {
    data = text ? JSON.parse(text) : null
  } catch {
    jsonLesbar = false
  }

  if (!res.ok) {
    const err = (data as ErrorBody)?.error
    throw new ApiError(
      err?.code ?? (res.status === 429 || res.status === 503 ? 'rate_limited' : 'internal'),
      err?.message ?? menschlich(res.status),
      res.status,
      err?.requestId,
      err?.data,
    )
  }

  // 200, aber kein JSON: Das ist kein Erfolg, sondern etwas, das wir nicht
  // verstehen — etwa eine untergeschobene Fehlerseite.
  if (!jsonLesbar) {
    throw new ApiError('internal', t('Unerwartete Antwort vom Server.'), res.status)
  }

  return data as T
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = { accept: 'application/json' }
  if (body !== undefined) headers['content-type'] = 'application/json'
  if (method !== 'GET' && method !== 'HEAD') headers[CSRF_HEADER] = csrfToken()

  let res: Response
  try {
    res = await fetch(`/api/v1${path}`, {
      method,
      headers,
      credentials: 'same-origin',
      body: body === undefined ? null : JSON.stringify(body),
    })
  } catch {
    // Netzwerkfehler sind für den Nutzer dasselbe wie „offline" — und genau so
    // soll es in der Oberfläche stehen, nicht als „TypeError: Failed to fetch".
    throw new ApiError('offline', t('Keine Verbindung zum Server.'), 0)
  }

  return auswerten<T>(res)
}

/**
 * Rohe Bytes senden — nur für Datei-Blöcke.
 *
 * Eigener Pfad statt eines Sonderfalls in `request`: Dort wird jeder Body
 * durch `JSON.stringify` geschickt, was einen ArrayBuffer stillschweigend zu
 * `{}` machen würde. Ein Upload, der leere Blöcke sendet und dabei „ok" meldet,
 * ist schlimmer als einer, der scheitert.
 */
async function requestRaw<T>(
  method: string,
  path: string,
  body: ArrayBuffer,
  opts: { signal?: AbortSignal } = {},
): Promise<T> {
  let res: Response
  try {
    res = await fetch(`/api/v1${path}`, {
      method,
      headers: {
        accept: 'application/json',
        'content-type': 'application/octet-stream',
        [CSRF_HEADER]: csrfToken(),
      },
      credentials: 'same-origin',
      body,
      ...(opts.signal ? { signal: opts.signal } : {}),
    })
  } catch {
    throw new ApiError('offline', t('Keine Verbindung zum Server.'), 0)
  }

  return auswerten<T>(res)
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body: unknown = {}) => request<T>('POST', path, body),
  put: <T>(path: string, body: unknown = {}) => request<T>('PUT', path, body),
  patch: <T>(path: string, body: unknown = {}) => request<T>('PATCH', path, body),
  del: <T>(path: string) => request<T>('DELETE', path),
  putRaw: <T>(path: string, body: ArrayBuffer, opts?: { signal?: AbortSignal }) =>
    requestRaw<T>('PUT', path, body, opts),
}

// --- Typen der Antworten -----------------------------------------------------

export interface Me {
  personId: string
  familyId: string
  role: string
  displayName: string
  permissions: string[]
  prefs: { scale: string; mode: string; perf: string }
  recoveryCodesLeft: number
}

export interface Device {
  id: string
  personId: string
  name: string
  platform: string | null
  lastSeenAt: string | null
  blockedAt: string | null
  verifiedAt: string | null
  activeSessions: number
  isCurrent: boolean
}

/**
 * Gerätename für die Anmeldung. Bewusst grob: „Chrome auf Windows" ist für die
 * Geräteliste verständlich, ohne einen Fingerabdruck des Browsers zu bauen.
 */
export function deviceHint(): { name: string; platform: string } {
  const ua = navigator.userAgent
  const browser =
    /Edg\//.test(ua) ? 'Edge' :
    /OPR\//.test(ua) ? 'Opera' :
    /Firefox\//.test(ua) ? 'Firefox' :
    /Chrome\//.test(ua) ? 'Chrome' :
    /Safari\//.test(ua) ? 'Safari' : 'Browser'
  const os =
    /Android/.test(ua) ? 'Android' :
    /iPhone|iPad/.test(ua) ? 'iPhone/iPad' :
    /Windows/.test(ua) ? 'Windows' :
    /Mac OS/.test(ua) ? 'Mac' :
    /Linux/.test(ua) ? 'Linux' : 'Gerät'
  return { name: `${browser} auf ${os}`, platform: os }
}
